import type { ReactNode } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import type { ConversationStarter } from '@atlaskit/rovo-agent-components/ui/AgentConversationStarters';
import { type OptionType } from '@atlaskit/select';
import { type Value } from '@atlaskit/smart-user-picker';
import { type AgentAction } from '@atlassian/conversation-assistant-service-api/types/agent-actions';
import {
	type Agent,
	type AgentKnowledgeConfiguration,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
export type ConfluenceFilterSelectType = OptionType & {
	value: 'all' | 'spaces' | 'pages';
};

export type KnowledgeFilterSelectType = OptionType & {
	ari: string;
	iconUrl?: string;
	renderIcon?: () => React.ReactNode;
	spaceKey?: string;
	url: string;
};

export type AgentFormFields = {
	name: string;
	description: string;
	instructions: string;
	conversationStarters: string[];
	agentActions: AgentAction[];
	creator: Value;
	knowledgeScope: boolean;
	confluenceFilterScope?: ConfluenceFilterSelectType;
	confluenceAncestorPageFilters?: KnowledgeFilterSelectType[];
	confluenceSpaceFilters?: KnowledgeFilterSelectType[];
	jiraProjectFilters?: KnowledgeFilterSelectType[];
	jsmProjectFilters?: KnowledgeFilterSelectType[];
	knowledgeEnabledFields: KnowledgeEnabledFields;
};

export type AgentFormFieldsManualSetup = Pick<
	AgentFormFields,
	'name' | 'description' | 'instructions' | 'conversationStarters'
>;

type KnowledgeEnabledFields = {
	[K in AgentKnowledgeConfiguration['source']]?: boolean;
};

export type ValidAgentFormFields = keyof AgentFormFields;

export const isSystemAgent = (agent: Agent): boolean => agent.creator_type === 'SYSTEM';
export const isCustomerAgent = (agent: Agent): boolean => agent.creator_type === 'CUSTOMER';
// BE will deprecate THIRD_PARTY and use FORGE instead
/**
 * @private
 * @deprecated Use isForgeAgentByCreatorType from @atlaskit/rovo-agent-components/common/utils/is-forge-agent instead
 * @param agent
 * @returns boolean
 */
export const isForgeAgent = (agent: Agent): boolean =>
	agent.creator_type === 'THIRD_PARTY' ||
	agent.creator_type === 'FORGE' ||
	(agent.creator_type === 'REMOTE_A2A' && fg('rovo_agent_support_a2a_avatar'));
export const isPrivateAgent = (agent: Agent): boolean => agent.visibility === 'PRIVATE';
export const isOOTBAgent = (agent: Agent): boolean => agent.creator_type === 'OOTB';
export const BrowseCategories = {
	ALL_AGENTS: 'all-agents',
	STARRED_AGENTS: 'starred-agents',
	MY_AGENTS: 'my-agents',
	TEMPLATES: 'templates',
	VERIFIED_AGENTS: 'verified-agents',
} as const;
export type BrowseCategory = (typeof BrowseCategories)[keyof typeof BrowseCategories];

export type AgentTabs =
	| 'all-agents'
	| 'starred-agents'
	| 'my-agents'
	| 'templates'
	| 'verified-agents';

export type UIContext = 'agents-modal' | 'agents-sidebar' | 'agents-fullscreen';

export type BrowseAgentsModalProps = {
	onClose: () => void;
	siteId: string;
	onClickChat: (agent: Agent) => void;
	handleClickConversationStarter: (agent: Agent, conversationStarter: ConversationStarter) => void;
	browseCategory?: BrowseCategory;
	agentTabs?: AgentTabs[];
	agentCardRole?: 'link' | 'button';
	showCreateAgentButtonInHeader?: boolean;
} & AssistanceServiceInputParam;

type BrowseAgentsExtendedProps = {
	chatRefreshHeader?: ReactNode;
	singleColumn?: boolean;
};

export type BrowseAgentsSidebarProps = BrowseAgentsModalProps &
	BrowseAgentsExtendedProps & {
		titleElement: ReactNode;
	};

export type BrowseAgentsModalPropsWithoutOnClose = Omit<BrowseAgentsModalProps, 'onClose'>;

export type BrowseAgentsFullscreenProps = Omit<
	BrowseAgentsModalPropsWithoutOnClose & BrowseAgentsExtendedProps,
	'chatRefreshHeader'
>;
